import { FC } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from '@kontentino/ui';
import PendingBadge from 'app/components/PendingBadge';
import usePendingInvitationStore from '../hooks/usePendingInvitationStore';
import { PendingInvitation } from '../types/accountSwitcher';

type Props = {
  invitations: PendingInvitation[];
  onSelect?: () => void;
  compact?: boolean;
};

const PendingInvitationMenuItems: FC<Props> = ({
  invitations,
  onSelect,
  compact = false,
}) => {
  const { t } = useTranslation();
  const open = usePendingInvitationStore((state) => state.open);

  if (invitations.length === 0) return null;

  return (
    <>
      <DropdownMenu.Separator />
      <div
        className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-grayscale-100"
        data-cy="account-switcher_invitations-heading"
      >
        {t('accountSwitcher.invitation.menuHeading')}
      </div>
      {invitations.map((invitation) => {
        const role = t(`userRoles.${invitation.role}`);
        const badge = (
          <PendingBadge
            className={compact ? 'tw-mt-1 tw-self-start' : 'tw-shrink-0'}
          >
            {t('accountSwitcher.invitation.pendingBadge')}
          </PendingBadge>
        );

        return (
          <DropdownMenu.Item
            key={invitation.account_id}
            className={clsx(
              'tw-h-auto tw-rounded-lg tw-p-2',
              !compact && 'tw-justify-between',
            )}
            onSelect={() => {
              onSelect?.();
              open(invitation.account_id);
            }}
            data-cy={`account-switcher_invitation-item-${invitation.account_id}`}
          >
            <span className="tw-flex tw-min-w-0 tw-flex-col">
              <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                {invitation.account_name}
              </span>
              <span className="tw-text-[10px] tw-text-grayscale-100">
                {invitation.inviter_name
                  ? t('accountSwitcher.invitation.menuSubtitle', {
                      role,
                      inviter: invitation.inviter_name,
                    })
                  : t('accountSwitcher.invitation.menuSubtitleNoInviter', {
                      role,
                    })}
              </span>
              {compact && badge}
            </span>
            {!compact && badge}
          </DropdownMenu.Item>
        );
      })}
    </>
  );
};

export default PendingInvitationMenuItems;
